/**
 * My Financial Assistant (TMMAPP-0142 U1; TMMAPI-0181 U1 and U2, MM-D-146): a person's scoped AI
 * chat sessions. Wire types from `src/api/generated/`, normalized into the module's domain types.
 *
 * - **Options:** the category registry and what the caller can scope now (`scopable`, with
 *   `shared` on a granted shared virtual account).
 * - **Sessions:** create, list, read, patch (only the fields given), archive, restore, delete.
 * - **Turns:** a message answers `202 {turnId}`; the turn is polled until `DONE`, `NEEDS_SCOPE` or
 *   `FAILED`. A `NEEDS_SCOPE` turn resumes with `continue`, after a PATCH (allow) or `decline-scope`.
 * - **Privacy:** what is in scope, what is never sent, and every call sent verbatim with its charge.
 * - **Shared sessions** (TMMAPP-0142 U3; TMMAPI-0181 U6): `visibility: SHARED` on one virtual
 *   account, listed for every member with assistant access. Messages carry their author; a member
 *   posts to the others (`askAi: false`, free) or asks the assistant (`askAi: true`; paid by the
 *   virtual account's owner since TMMAPI-0184 U3). Each member adds only their own private data (`PUT …/private-data`, the whole set), shown
 *   to everyone as the session's banner. `unreadCount` per session, `POST …/read {upToId}`.
 *
 * - **Attachments** (TMMAPP-0142 U5; TMMAPI-0181 U7): a file is uploaded first (`POST …/attachments`,
 *   multipart with the browser's boundary), then named on the message (`attachmentIds`). An upload
 *   on no message is the uploader's to delete, and is swept after 24 h. Files download through
 *   `GET …/attachments/{id}` (bearer only, so through `download()`, never a plain link).
 *
 * - **Help & feedback** (TMMAPP-0142 U2; TMMAPI-0181 U4): the person's one `kind: SUPPORT` session,
 *   made or found by `POST …/sessions/support/ensure`. It reads no financial data; a support turn
 *   may end `NEEDS_CONFIRMATION` with a `ticketDraft`, filed only when the person confirms it
 *   (`ticketsApi.create` with `draftTurnId`).
 *
 * - **The transcript as served** (TMMAPP-0142 U6; TMMAPI-0182 U2): each answer carries its own charge
 *   (`chargedMinor`, `currency`) and, in a shared session, who asked (`askedBy`); a scope request is a
 *   `SCOPE_REQUEST` line (`scopeRequest`, `outcome`), so its card survives a reload; the session
 *   view serves its `members[]` and its open `runningTurn` (every session).
 *
 * - **Connected AI tools** (TMMAPP-0142 U7; TMMAPI-0183 U1, MM-D-148): the session view's
 *   `agentGrants[]` (the caller's own tools only), and the privacy panel's `agentCalls[]` (every
 *   member's, free: the tool's model isn't ours). A Help & feedback answer that drafted a ticket
 *   carries `ticketDraft` and, once filed, `ticketNumber` (both omitted when none).
 *
 * - **Breakout rooms** (TMMAPP-0148 U1; TMMAPI-0184 U1, MM-D-149): a member's own private room on a
 *   shared session (`visibility: BREAKOUT`), opened with `POST …/sessions/{sharedId}/breakout`
 *   (made or returned). The shared session's view names the caller's own (`myBreakoutId`); the
 *   room names its parent (`parentSessionId`, `parentName`, `parentAvailable`). An answer there may
 *   carry a `replyDraft`, which the server never posts.
 * - **Who pays** (TMMAPI-0184 U3): every session anchored to a virtual account states `paidBy`
 *   (its owner); a refusal of the payer's credit is `PAYER_UNAVAILABLE {reason}`, never a figure.
 *   A member's own allowance there is `myMonthlyLimit` (TMMAPI-0186 U1); reaching it fails the turn
 *   `MEMBER_LIMIT` (`409 AI_DISABLED {reason: MEMBER_LIMIT}` on a send), with no figure.
 *
 * Usage is money only (MM-D-140): this module carries no token count. Nothing here is stored in the
 * browser; the transcript lives in memory for the page that shows it.
 */
import type { AxiosRequestConfig } from 'axios';
import { accessOf, type AgentAccess } from './agentConnections';
import { download, request, type DownloadResult } from './client';
import { ErrorCodes, isApiError } from './errors';
import type {
  AssistantAgentCall as WireAgentCall,
  AssistantAgentGrant as WireAgentGrant,
  AssistantAttachment as WireAttachment,
  AssistantCategoryOption as WireCategoryOption,
  AssistantMessage as WireMessage,
  AssistantMessageAccepted,
  AssistantMessagePage as WireMessagePage,
  AssistantMessageRequest,
  AssistantRunningTurn as WireRunningTurn,
  AssistantOmittedAttachment as WireOmittedAttachment,
  AssistantOptions as WireOptions,
  AssistantPrivacy as WirePrivacy,
  AssistantPrivateData as WirePrivateData,
  AssistantPrivateDataRequest,
  AssistantReadRequest,
  AssistantScopable as WireScopable,
  AssistantScopeRequest as WireScopeRequest,
  AssistantScopeView as WireScopeView,
  AssistantSearchHit as WireSearchHit,
  AssistantSearchPage as WireSearchPage,
  AssistantSentAttachment as WireSentAttachment,
  AssistantSentCall as WireSentCall,
  AssistantSession as WireSession,
  AssistantSessionCreateRequest,
  AssistantSessionMember as WireSessionMember,
  AssistantSessionPatchRequest,
  AssistantTicketDraft as WireTicketDraft,
  AssistantTurn as WireTurn,
  AssistantTurnAccepted,
} from './generated';

export type AssistantScopeKind = 'ACCOUNT' | 'VIRTUAL_ACCOUNT' | 'ENVELOPE';
export type AssistantTier = 'FAST' | 'DEEP';
export type AssistantSessionStatus = 'ACTIVE' | 'ARCHIVED';
export type AssistantTurnStatus =
  'RUNNING' | 'NEEDS_SCOPE' | 'NEEDS_CONFIRMATION' | 'DONE' | 'FAILED';
/** `SUPPORT`: the person's one Help & feedback session; `ASSISTANT`: every other. */
export type AssistantSessionKind = 'ASSISTANT' | 'SUPPORT';
export type TicketType = 'SUPPORT_CASE' | 'FEATURE_REQUEST';
/** `SCOPE_REQUEST`: the assistant asked to see more; never a message to answer (TMMAPI-0182 U2). */
export type AssistantMessageRole = 'USER' | 'ASSISTANT' | 'SCOPE_REQUEST';
/** A scope request line's outcome (`SUPERSEDED` also for a waiting turn that ended otherwise, G1). */
export type AssistantScopeOutcome =
  'PENDING' | 'DECLINED' | 'ACCEPTED' | 'PARTIAL' | 'NOT_CHANGED' | 'SUPERSEDED';
/** `BREAKOUT`: the caller's own private room on a shared session (TMMAPI-0184 U1). */
export type AssistantVisibility = 'PRIVATE' | 'SHARED' | 'BREAKOUT';
export type AssistantAttachmentKind = 'IMAGE' | 'PDF' | 'SHEET';

const SCOPE_KINDS: ReadonlySet<string> = new Set(['ACCOUNT', 'VIRTUAL_ACCOUNT', 'ENVELOPE']);
const TURN_STATUSES: ReadonlySet<string> = new Set([
  'RUNNING',
  'NEEDS_SCOPE',
  'NEEDS_CONFIRMATION',
  'DONE',
  'FAILED',
]);
const ATTACHMENT_KINDS: ReadonlySet<string> = new Set(['IMAGE', 'PDF', 'SHEET']);
const SCOPE_OUTCOMES: ReadonlySet<string> = new Set([
  'PENDING',
  'DECLINED',
  'ACCEPTED',
  'PARTIAL',
  'NOT_CHANGED',
  'SUPERSEDED',
]);

function isScopeKind(value: string): value is AssistantScopeKind {
  return SCOPE_KINDS.has(value);
}

function isTurnStatus(value: string): value is AssistantTurnStatus {
  return TURN_STATUSES.has(value);
}

function isAttachmentKind(value: string): value is AssistantAttachmentKind {
  return ATTACHMENT_KINDS.has(value);
}

function isScopeOutcome(value: string): value is AssistantScopeOutcome {
  return SCOPE_OUTCOMES.has(value);
}

/** An attachment's kind; a kind this build doesn't know reads as a plain file (null). */
function attachmentKindOf(value: string): AssistantAttachmentKind | null {
  return isAttachmentKind(value) ? value : null;
}

export interface AssistantScopeEntity {
  kind: AssistantScopeKind;
  id: number;
}

export interface AssistantCategoryOption {
  /** The registry key (`TRANSACTIONS`, `PEOPLE_NAMES`, …). */
  key: string;
  labelKey: string;
  descriptionKey: string;
  defaultEnabled: boolean;
  /** Holdings and net worth: sent only in the owner's own session. */
  subscriberPrivate: boolean;
  /** A sharer's session may turn it on (published terms only). */
  sharerAllowed: boolean;
  /**
   * In a shared session, each member's own opt-in over their own data (`HOLDINGS`, `NET_WORTH`,
   * `PEOPLE_NAMES`), added through private data, never a session switch.
   */
  memberPrivate: boolean;
  /**
   * The tools the key still allows in a sharer's or a shared session although it isn't
   * sharer-allowed (`PLANNED`: `virtual_account_plan`, the virtual account's plan only).
   */
  sharerTools: string[];
}

export interface AssistantScopable extends AssistantScopeEntity {
  name: string;
  /** A virtual account shared with the caller, with the owner's assistant grant. */
  shared: boolean;
  /** An envelope's virtual account; null for anything else. */
  virtualAccountId: number | null;
}

export interface AssistantOptions {
  categories: AssistantCategoryOption[];
  scopable: AssistantScopable[];
  tiers: AssistantTier[];
}

/** An entity in a session's scope as the server serves it; `name` is null once it's gone. */
export interface AssistantScopeItem extends AssistantScopeEntity {
  name: string | null;
  /** Still in the caller's reach; an unavailable one stays listed and is out of scope. */
  available: boolean;
}

/** A member's own private data in a shared session, as the banner shows it. */
export interface AssistantPrivateData {
  kind: 'ACCOUNT' | 'CATEGORY';
  /** ACCOUNT: the account's id, served to the member who added it only. */
  accountId: number | null;
  accountName: string | null;
  last4: string | null;
  /** CATEGORY: `HOLDINGS`, `NET_WORTH` or `PEOPLE_NAMES`. */
  category: string | null;
  /** The member who added it (their display name). */
  memberName: string | null;
  /** The caller added it, and may remove it. */
  mine: boolean;
}

/** Why a member can't ask the assistant (TMMAPI-0195 U4). */
export type AssistantAskReason = 'NOT_ALLOWED' | 'LIMIT_REACHED' | 'AI_OFF';
const ASK_REASONS: ReadonlySet<string> = new Set(['NOT_ALLOWED', 'LIMIT_REACHED', 'AI_OFF']);

/** A served ask reason; an unknown or absent one is `null`. */
export function askReasonOf(value: unknown): AssistantAskReason | null {
  return typeof value === 'string' && ASK_REASONS.has(value) ? (value as AssistantAskReason) : null;
}

/**
 * Why an ask was refused because the caller can't ask (TMMAPI-0195 U4): `403 ASSISTANT_ASK_NOT_ALLOWED
 * {reason}` by its reason (an unknown one is `NOT_ALLOWED`), and the member limit's `409 AI_DISABLED
 * {reason: MEMBER_LIMIT}` as `LIMIT_REACHED`; null for anything else.
 */
export function askRefusalReason(error: unknown): AssistantAskReason | null {
  if (!isApiError(error)) return null;
  const backend = error.details.backend;
  const reason = typeof backend === 'object' && backend !== null ? backend.reason : undefined;
  if (error.code === ErrorCodes.ASSISTANT_ASK_NOT_ALLOWED)
    return askReasonOf(reason) ?? 'NOT_ALLOWED';
  if (error.code === ErrorCodes.AI_DISABLED && reason === MEMBER_LIMIT) return 'LIMIT_REACHED';
  return null;
}

/**
 * A shared session's member now (TMMAPI-0195 U4, MM-D-149 amended): the owner and every ACTIVE share
 * of the virtual account, guests included.
 */
export interface AssistantSessionMember {
  /** Their display name; null when not served. */
  name: string | null;
  /** The virtual account's owner. */
  owner: boolean;
  /**
   * They may ask the assistant, on the owner's credit: the owner when AI is on; anyone else with the
   * owner's grant, their own AI on and their member limit not used up. No subscription of their own.
   */
  canAsk: boolean;
  /**
   * Why not, served only on the caller's own row and, to the owner, on every row; null when
   * `canAsk` or not the caller's to see.
   */
  canAskReason: AssistantAskReason | null;
  /** The caller. */
  mine: boolean;
}

/** The session's open turn, `RUNNING` or `NEEDS_SCOPE` (served on every session). */
export interface AssistantRunningTurn {
  turnId: number;
  /** The caller asked it: the one turn to follow. */
  mine: boolean;
  /** Who asked it; null when not served. */
  askedByName: string | null;
}

export interface AssistantSession {
  id: number;
  /** `SUPPORT` is recognised by its kind, never its name; the app shows its own label for it. */
  kind: AssistantSessionKind;
  name: string;
  status: AssistantSessionStatus;
  /** `SHARED`: anchored to `virtualAccountId`, listed for every member with assistant access. */
  visibility: AssistantVisibility;
  /** The caller created it. */
  mine: boolean;
  /** The caller may rename it, change its categories, archive, restore or delete it. */
  canManage: boolean;
  /** Messages after the caller's read marker that the caller didn't write. */
  unreadCount: number;
  /** A shared session's banner: each member's own private data added to it. */
  privateData: AssistantPrivateData[];
  defaultTier: AssistantTier;
  scope: AssistantScopeItem[];
  categories: Record<string, boolean>;
  /** A sharer's session whose share or grant ended: read-only (`409 ASSISTANT_ACCESS_ENDED`). */
  accessEnded: boolean;
  /** Set on a sharer's session (the shared virtual account it reads) and on a shared session. */
  virtualAccountId: number | null;
  /** What this session's AI calls debited the wallet, in the wallet's minor units. */
  chargedMinor: number;
  lastTurnChargedMinor: number;
  /** How full the context was on the last call, 0 to 100. */
  contextFillPercent: number;
  /** The wallet's currency; null when the person has no wallet. */
  currency: string | null;
  createdAt: string;
  updatedAt: string;
  /** A shared session's members, owner first; empty on a private session. */
  members: AssistantSessionMember[];
  /** The open turn while one runs or waits; null when none. */
  runningTurn: AssistantRunningTurn | null;
  /** This session's grants to the caller's own connected AI tools, one per connection. */
  agentGrants: AssistantAgentGrant[];
  /** A shared session: the caller's own breakout room on it, once opened; never another member's. */
  myBreakoutId: number | null;
  /** A breakout: its shared session; null on every other session. */
  parentSessionId: number | null;
  /** A breakout: its shared session's name while it reads for the caller; null otherwise. */
  parentName: string | null;
  /**
   * A breakout: its shared session still reads for the caller. False: the room is read-only (and
   * `accessEnded`), and may only be deleted. True on every other session.
   */
  parentAvailable: boolean;
  /**
   * Who pays for this session's AI calls (TMMAPI-0184 U3): the virtual account's owner on a session
   * anchored to one; null on a person's own private session and Help & feedback.
   */
  paidBy: AssistantPaidBy | null;
  /**
   * The caller's own monthly allowance on the owner's AI credit (TMMAPI-0186 U1), on a session
   * anchored to someone else's virtual account, in the session's `currency`; null when there's none
   * (and on every session the caller pays for). Never a figure of the owner's wallet.
   */
  myMonthlyLimit: AssistantMemberLimit | null;
}

/** A member's monthly allowance on an anchored session: the limit and what their asks used of it. */
export interface AssistantMemberLimit {
  limitMinor: number;
  usedMinor: number;
}

/** An anchored session's payer: the virtual account owner's display name, and whether it's the caller. */
export interface AssistantPaidBy {
  name: string | null;
  mine: boolean;
}

/** One of the caller's connected AI tools granted this session (TMMAPI-0183 U1). */
export interface AssistantAgentGrant {
  connectionId: number;
  label: string;
  access: AgentAccess;
}

/** A file in a session (TMMAPI-0181 U7): an upload, or one on a message. */
export interface AssistantAttachment {
  attachmentId: number;
  name: string;
  /** Its type as its content says (never its name). */
  contentType: string;
  /** null for a kind this build doesn't know: shown as a plain file. */
  kind: AssistantAttachmentKind | null;
  sizeBytes: number;
  /** PDF: its pages, when counted. */
  pages: number | null;
  /** SHEET: the rows of the table the assistant receives. */
  rows: number | null;
  /** SHEET: the table was cut to the bounds. */
  truncated: boolean;
  /** Images and PDFs reach the assistant as they are (unredacted); a sheet's text is masked. */
  sentAsIs: boolean;
  /** The caller uploaded it. */
  mine: boolean;
  /** Who uploaded it (their display name); null when not served. */
  uploaderName: string | null;
  /** On no message yet: when the server sweeps it (24 h after the upload); null once sent. */
  expiresAt: string | null;
}

/** An attachment a call sent, as the privacy panel lists it. */
export interface AssistantSentAttachment {
  attachmentId: number;
  name: string;
  kind: AssistantAttachmentKind | null;
  sizeBytes: number;
  pages: number | null;
  rows: number | null;
  truncated: boolean;
}

/** Another member's attachment a call left out, and why (`REQUEST_BUDGET`, …, verbatim). */
export interface AssistantOmittedAttachment {
  attachmentId: number;
  name: string;
  kind: AssistantAttachmentKind | null;
  sizeBytes: number;
  reason: string;
}

export interface AssistantMessage {
  id: number;
  role: AssistantMessageRole;
  text: string;
  turnId: number | null;
  createdAt: string;
  /** Who wrote it; null for the assistant's answers. */
  authorName: string | null;
  /** The caller wrote it. */
  mine: boolean;
  /** A question to the assistant (it has a turn); false for a message to the other members only. */
  askedAi: boolean;
  /** The files on the message (empty when none). */
  attachments: AssistantAttachment[];
  /** An answer: what its turn charged its asker, in `currency`; null on any other line. */
  chargedMinor: number | null;
  /** An answer: the asker's wallet currency; null without a wallet, and on any other line. */
  currency: string | null;
  /** An answer in a shared session: who asked (and paid); null otherwise. */
  askedBy: { name: string | null; mine: boolean } | null;
  /**
   * A `SCOPE_REQUEST` line: what the assistant asked to add (its `text` is the reason). Null on any
   * other line, and on a purged one (its text is then the removal marker, G2).
   */
  scopeRequest: AssistantScopeRequest | null;
  /** A `SCOPE_REQUEST` line's outcome; null on any other line, or for one this build doesn't know. */
  outcome: AssistantScopeOutcome | null;
  /** A Help & feedback answer that drafted a ticket: the draft (it survives a reload); else null. */
  ticketDraft: AssistantTicketDraft | null;
  /** The number of the caller's ticket filed from this answer's turn, bare; null when none. */
  ticketNumber: number | null;
  /** A breakout's answer: the message it suggests for the shared session (never posted); else null. */
  replyDraft: string | null;
}

/** A sent message: the turn to poll when it asked the assistant, else null. */
export interface AssistantMessageSent {
  messageId: number;
  turnId: number | null;
}

export interface AssistantMessagePage {
  items: AssistantMessage[];
  hasMore: boolean;
  /** `aroundId` only (TMMAPI-0185 U1): newer lines remain after the page; false otherwise. */
  hasNewer: boolean;
}

/** Where a transcript page is read from: at most one id (TMMAPI-0184 U2); none is the newest page. */
export type AssistantMessagesAt = { afterId: number } | { beforeId: number } | { aroundId: number };

/** A search hit: one transcript line holding the query (TMMAPI-0184 U2). */
export interface AssistantSearchHit {
  /** The line's id: `aroundId` opens it. */
  id: number;
  role: AssistantMessageRole;
  /** About 120 characters around the match, as plain text. */
  snippet: string;
  createdAt: string;
  turnId: number | null;
  /** Across sessions only; null in a session's own search. */
  sessionId: number | null;
  sessionName: string | null;
}

export interface AssistantSearchPage {
  /** Newest first. */
  items: AssistantSearchHit[];
  /** Older hits remain: in a session, page back with `beforeId` = the last hit's id. */
  hasMore: boolean;
  /**
   * Across sessions, while `hasMore` (TMMAPI-0185 U1): the opaque cursor sent back as `beforeId`
   * for the next page, as served (never built or parsed); null otherwise.
   */
  nextBeforeId: string | null;
}

export interface AssistantScopeRequest {
  categories: string[];
  entities: AssistantScopeEntity[];
  reason: string;
}

/** The ticket a support turn drafted (`NEEDS_CONFIRMATION`); nothing is filed until confirmed. */
export interface AssistantTicketDraft {
  type: TicketType;
  title: string;
  description: string;
}

export interface AssistantTurn {
  id: number;
  sessionId: number;
  status: AssistantTurnStatus;
  tier: AssistantTier;
  answer: string | null;
  scopeRequest: AssistantScopeRequest | null;
  scopeDeclined: boolean;
  chargedMinor: number;
  currency: string | null;
  /** FAILED: the served reason, verbatim (`INTERRUPTED`, `WALLET_EMPTY`, …); null otherwise. */
  failureReason: string | null;
  /** `PAYER_UNAVAILABLE`: the payer's served reason (`MONTHLY_LIMIT`, `BUSY`, …); null otherwise. */
  payerReason: string | null;
  /** The AI gate's `debtMinor` on `AI_DEBT`; null when not served. */
  debtMinor: number | null;
  /** The AI gate's `firstPurchaseRequired` on `WALLET_EMPTY`. */
  firstPurchaseRequired: boolean;
  /** The AI gate's `availableMinor` on `WALLET_EMPTY`; null when not served. */
  availableMinor: number | null;
  /** `NEEDS_CONFIRMATION`: the drafted ticket; null otherwise. */
  ticketDraft: AssistantTicketDraft | null;
  /** `ATTACHMENT_TOO_LARGE`: the limit reached (`CONTEXT_WINDOW`, …); null when not served. */
  failureLimitKind: string | null;
}

export interface AssistantDisclosureCounts {
  peopleMasked: number;
  namesMasked: number;
  excludedLinesOmitted: number;
  contributionsWithheld: number;
  redactorMasks: number;
}

export interface AssistantToolCall {
  id: string;
  name: string;
  /** The tool's input and result as JSON text, for a mono block. */
  input: string;
  result: string;
}

export interface AssistantSentCall {
  aiRequestId: number;
  turnId: number;
  seq: number;
  /** `CONVERSE` or `SUMMARY`; null for a kind this build doesn't know. */
  kind: 'CONVERSE' | 'SUMMARY' | null;
  createdAt: string;
  chargedMinor: number;
  /** The verbatim outbound body sent to the provider. */
  outboundPayload: string;
  toolCalls: AssistantToolCall[];
  /** Who asked (they paid `chargedMinor`); null when not served. */
  askedBy: string | null;
  /** Purged because a contributor's data was erased: the payload and tool calls say so. */
  contentRemoved: boolean;
  /** The files this call sent (empty when none, and once purged). */
  attachments: AssistantSentAttachment[];
  /** Other members' files this call left out, with the reason. */
  omittedAttachments: AssistantOmittedAttachment[];
}

/** A call a connected AI tool made in this session (TMMAPI-0183 U1): free, so it has no charge. */
export interface AssistantAgentCall {
  /** The connection's label (as it was when the call was made, once disconnected). */
  connectionLabel: string;
  /** The tool it called (an identifier). */
  tool: string;
  /** What it sent and what it was given, as JSON text for a mono block. */
  input: string;
  answer: string;
  at: string;
  /** A write tool's call (always false until TMMAPI-0183 U2). */
  write: boolean;
  /** Whose AI tool it was. */
  askedBy: { name: string | null; mine: boolean };
  /** Purged (a contributor's data left or was erased): input and answer are the removal marker. */
  contentRemoved: boolean;
}

export interface AssistantPrivacy {
  inScope: {
    entities: AssistantScopeItem[];
    categoriesOn: string[];
    categoriesOff: string[];
    /** What else the calls send, as codes (`MEMBER_FIRST_NAMES` on a shared session and a breakout). */
    alsoSent: string[];
    /** A shared session's members' own private data, as its banner shows it. */
    privateData: AssistantPrivateData[];
  };
  neverSent: {
    /** Fixed statements, as codes (`ACCOUNT_NUMBERS`, `EMAILS`, …). */
    statements: string[];
    counts: AssistantDisclosureCounts;
  };
  sent: AssistantSentCall[];
  /** Earlier turns stop being sent from this turn on; null when none. */
  historyFromTurnId: number | null;
  /** Every call a connected AI tool made here, oldest first (empty when read for one turn). */
  agentCalls: AssistantAgentCall[];
}

function visibilityOf(value: string): AssistantVisibility {
  return value === 'SHARED' || value === 'BREAKOUT' ? value : 'PRIVATE';
}

function tierOf(value: string): AssistantTier {
  return value === 'DEEP' ? 'DEEP' : 'FAST';
}

function normalizeCategory(w: WireCategoryOption): AssistantCategoryOption {
  return {
    key: w.key,
    labelKey: w.labelKey,
    descriptionKey: w.descriptionKey,
    defaultEnabled: w.defaultEnabled,
    subscriberPrivate: w.subscriberPrivate,
    sharerAllowed: w.sharerAllowed,
    memberPrivate: w.memberPrivate,
    sharerTools: [...w.sharerTools],
  };
}

function normalizeScopable(w: WireScopable): AssistantScopable[] {
  if (!isScopeKind(w.kind)) return [];
  return [
    {
      kind: w.kind,
      id: w.id,
      name: w.name,
      shared: w.shared,
      virtualAccountId: w.virtualAccountId,
    },
  ];
}

export function normalizeOptions(w: WireOptions): AssistantOptions {
  return {
    categories: w.categories.map(normalizeCategory),
    scopable: w.scopable.flatMap(normalizeScopable),
    tiers: w.tiers.map(tierOf),
  };
}

function normalizeScopeItem(w: WireScopeView): AssistantScopeItem[] {
  if (!isScopeKind(w.kind)) return [];
  return [{ kind: w.kind, id: w.id, name: w.name, available: w.available }];
}

function normalizePrivateData(w: WirePrivateData): AssistantPrivateData {
  return {
    kind: w.kind === 'CATEGORY' ? 'CATEGORY' : 'ACCOUNT',
    accountId: w.accountId,
    accountName: w.accountName,
    last4: w.last4,
    category: w.category,
    memberName: w.memberName,
    mine: w.mine,
  };
}

function normalizeMember(w: WireSessionMember): AssistantSessionMember {
  return {
    name: w.name,
    owner: w.owner,
    canAsk: w.canAsk,
    canAskReason: w.canAsk ? null : askReasonOf(w.canAskReason),
    mine: w.mine,
  };
}

function normalizeRunningTurn(w: WireRunningTurn | null): AssistantRunningTurn | null {
  return w === null ? null : { turnId: w.turnId, mine: w.mine, askedByName: w.askedByName };
}

function normalizeAgentGrant(w: WireAgentGrant): AssistantAgentGrant {
  return { connectionId: w.connectionId, label: w.label, access: accessOf(w.access) };
}

export function normalizeSession(w: WireSession): AssistantSession {
  return {
    id: w.id,
    kind: w.kind === 'SUPPORT' ? 'SUPPORT' : 'ASSISTANT',
    name: w.name,
    status: w.status === 'ARCHIVED' ? 'ARCHIVED' : 'ACTIVE',
    visibility: visibilityOf(w.visibility),
    mine: w.mine,
    canManage: w.canManage,
    unreadCount: Math.max(0, w.unreadCount),
    privateData: w.privateData.map(normalizePrivateData),
    defaultTier: tierOf(w.defaultTier),
    scope: w.scope.flatMap(normalizeScopeItem),
    categories: { ...w.categories },
    accessEnded: w.accessEnded,
    virtualAccountId: w.virtualAccountId,
    chargedMinor: w.chargedMinor,
    lastTurnChargedMinor: w.lastTurnChargedMinor,
    contextFillPercent: Math.min(100, Math.max(0, w.contextFillPercent)),
    currency: w.currency,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
    members: w.members.map(normalizeMember),
    runningTurn: normalizeRunningTurn(w.runningTurn),
    agentGrants: w.agentGrants.map(normalizeAgentGrant),
    // Each is omitted where it doesn't apply (TMMAPI-0184 U1 / U3).
    myBreakoutId: w.myBreakoutId ?? null,
    parentSessionId: w.parentSessionId ?? null,
    parentName: w.parentName ?? null,
    parentAvailable: w.parentAvailable !== false,
    paidBy: w.paidBy === undefined ? null : { name: w.paidBy.name, mine: w.paidBy.mine },
    myMonthlyLimit:
      w.myMonthlyLimit === undefined
        ? null
        : { limitMinor: w.myMonthlyLimit.limitMinor, usedMinor: w.myMonthlyLimit.usedMinor },
  };
}

export function normalizeAttachment(w: WireAttachment): AssistantAttachment {
  return {
    attachmentId: w.attachmentId,
    name: w.name,
    contentType: w.contentType,
    kind: attachmentKindOf(w.kind),
    sizeBytes: w.sizeBytes,
    pages: w.pages,
    rows: w.rows,
    truncated: w.truncated,
    sentAsIs: w.sentAsIs,
    mine: w.mine,
    uploaderName: w.uploaderName,
    expiresAt: w.expiresAt,
  };
}

function normalizeSentAttachment(w: WireSentAttachment): AssistantSentAttachment {
  return {
    attachmentId: w.attachmentId,
    name: w.name,
    kind: attachmentKindOf(w.kind),
    sizeBytes: w.sizeBytes,
    pages: w.pages,
    rows: w.rows,
    truncated: w.truncated,
  };
}

function normalizeOmittedAttachment(w: WireOmittedAttachment): AssistantOmittedAttachment {
  return {
    attachmentId: w.attachmentId,
    name: w.name,
    kind: attachmentKindOf(w.kind),
    sizeBytes: w.sizeBytes,
    reason: w.reason,
  };
}

function normalizeScopeRequest(w: WireScopeRequest): AssistantScopeRequest {
  return {
    categories: [...w.categories],
    entities: w.entities.flatMap((entity) =>
      isScopeKind(entity.kind) ? [{ kind: entity.kind, id: entity.id }] : [],
    ),
    reason: w.reason,
  };
}

function roleOf(value: string): AssistantMessageRole {
  return value === 'ASSISTANT' || value === 'SCOPE_REQUEST' ? value : 'USER';
}

function normalizeMessage(w: WireMessage): AssistantMessage {
  return {
    id: w.id,
    role: roleOf(w.role),
    text: w.text,
    turnId: w.turnId,
    createdAt: w.createdAt,
    authorName: w.authorName,
    mine: w.mine,
    askedAi: w.askedAi,
    attachments: w.attachments.map(normalizeAttachment),
    chargedMinor: w.chargedMinor,
    currency: w.currency,
    askedBy: w.askedBy === null ? null : { name: w.askedBy.name, mine: w.askedBy.mine },
    scopeRequest: w.scopeRequest === null ? null : normalizeScopeRequest(w.scopeRequest),
    outcome: w.outcome !== null && isScopeOutcome(w.outcome) ? w.outcome : null,
    // Omitted, never null, when there is none (TMMAPI-0183 U1 item 7a).
    ticketDraft: w.ticketDraft === undefined ? null : normalizeTicketDraft(w.ticketDraft),
    ticketNumber: w.ticketNumber ?? null,
    // Omitted, never null, when there is none (TMMAPI-0184 U1 item 6).
    replyDraft: w.replyDraft?.text ?? null,
  };
}

function normalizeSearchHit(w: WireSearchHit): AssistantSearchHit {
  return {
    id: w.id,
    role: roleOf(w.role),
    snippet: w.snippet,
    createdAt: w.createdAt,
    turnId: w.turnId,
    // Omitted in a session's own search (TMMAPI-0184 U2).
    sessionId: w.sessionId ?? null,
    sessionName: w.sessionName ?? null,
  };
}

function normalizeTicketDraft(w: WireTicketDraft | null): AssistantTicketDraft | null {
  if (w === null) return null;
  return {
    type: w.type === 'FEATURE_REQUEST' ? 'FEATURE_REQUEST' : 'SUPPORT_CASE',
    title: w.title,
    description: w.description,
  };
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeSearchPage(w: WireSearchPage): AssistantSearchPage {
  return {
    items: w.items.map(normalizeSearchHit),
    hasMore: w.hasMore,
    nextBeforeId:
      w.hasMore && typeof w.nextBeforeId === 'string' && w.nextBeforeId.length > 0
        ? w.nextBeforeId
        : null,
  };
}

/** The payer's refusal (TMMAPI-0184 U3): its details carry a reason only, never a figure. */
export const PAYER_UNAVAILABLE = 'PAYER_UNAVAILABLE';
/**
 * The member's own monthly allowance on the owner's credit is used up (TMMAPI-0186 U1): a turn's
 * `failureReason`, and a send's `409 AI_DISABLED {reason}`. A reason only, never a figure.
 */
export const MEMBER_LIMIT = 'MEMBER_LIMIT';

export function normalizeTurn(w: WireTurn): AssistantTurn {
  // A refusal of someone else's credit never carries a figure into the app, whatever is served:
  // its reason only (TMMAPI-0185 U1; the member's allowance, TMMAPI-0186 U1).
  const payer = w.failureReason === PAYER_UNAVAILABLE;
  const figureless = payer || w.failureReason === MEMBER_LIMIT;
  const details = figureless ? {} : (w.failureDetails ?? {});
  const payerReason = payer ? w.failureDetails?.reason : undefined;
  const request = w.scopeRequest;
  return {
    id: w.id,
    sessionId: w.sessionId,
    // An unknown status reads as still running: the poll keeps going until a known end.
    status: isTurnStatus(w.status) ? w.status : 'RUNNING',
    tier: tierOf(w.tier),
    answer: w.answer,
    scopeRequest: request === null ? null : normalizeScopeRequest(request),
    scopeDeclined: w.scopeDeclined,
    chargedMinor: w.chargedMinor,
    currency: w.currency,
    failureReason: w.failureReason,
    payerReason: typeof payerReason === 'string' && payerReason.length > 0 ? payerReason : null,
    debtMinor: finiteNumber(details.debtMinor),
    firstPurchaseRequired: details.firstPurchaseRequired === true,
    availableMinor: finiteNumber(details.availableMinor),
    ticketDraft: normalizeTicketDraft(w.ticketDraft),
    failureLimitKind: typeof details.limitKind === 'string' ? details.limitKind : null,
  };
}

function jsonText(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value ?? null, null, 2);
}

/**
 * A purged agent call serves the removed-content marker as a string in place of its input; a live
 * call's input is always the tool's JSON object (TMMAPI-0183 U1, G10).
 */
function isRemovedMarker(input: unknown): boolean {
  return typeof input === 'string';
}

function normalizeAgentCall(w: WireAgentCall): AssistantAgentCall {
  return {
    connectionLabel: w.connectionLabel,
    tool: w.tool,
    input: jsonText(w.input),
    answer: jsonText(w.answer),
    at: w.at,
    write: w.write,
    askedBy: { name: w.askedBy.name, mine: w.askedBy.mine },
    contentRemoved: isRemovedMarker(w.input),
  };
}

function normalizeSentCall(w: WireSentCall): AssistantSentCall {
  return {
    aiRequestId: w.aiRequestId,
    turnId: w.turnId,
    seq: w.seq,
    kind: w.kind === 'CONVERSE' || w.kind === 'SUMMARY' ? w.kind : null,
    createdAt: w.createdAt,
    chargedMinor: w.chargedMinor,
    outboundPayload: w.outboundPayload,
    toolCalls: w.toolCalls.map((call) => ({
      id: call.id,
      name: call.name,
      input: jsonText(call.input),
      result: jsonText(call.result),
    })),
    askedBy: w.askedBy,
    contentRemoved: w.contentRemoved,
    attachments: w.attachments.map(normalizeSentAttachment),
    omittedAttachments: w.omittedAttachments.map(normalizeOmittedAttachment),
  };
}

export function normalizePrivacy(w: WirePrivacy): AssistantPrivacy {
  return {
    inScope: {
      entities: w.inScope.entities.flatMap(normalizeScopeItem),
      categoriesOn: [...w.inScope.categoriesOn],
      categoriesOff: [...w.inScope.categoriesOff],
      // Absent (a pre-0206 server) reads as nothing else sent.
      alsoSent: [...(w.inScope.alsoSent ?? [])],
      privateData: w.inScope.privateData.map(normalizePrivateData),
    },
    neverSent: {
      statements: [...w.neverSent.statements],
      counts: {
        peopleMasked: w.neverSent.counts.peopleMasked,
        namesMasked: w.neverSent.counts.namesMasked,
        excludedLinesOmitted: w.neverSent.counts.excludedLinesOmitted,
        contributionsWithheld: w.neverSent.counts.contributionsWithheld,
        redactorMasks: w.neverSent.counts.redactorMasks,
      },
    },
    sent: w.sent.map(normalizeSentCall),
    historyFromTurnId: w.historyFromTurnId,
    agentCalls: w.agentCalls.map(normalizeAgentCall),
  };
}

const BASE = '/v1/me/assistant';

export interface AssistantSessionCreate {
  name: string;
  scope: AssistantScopeEntity[];
  categories: Record<string, boolean>;
  defaultTier?: AssistantTier | undefined;
  /** `SHARED`: the scope is exactly one virtual account; absent is private. */
  visibility?: AssistantVisibility | undefined;
}

/** What a message asks: `askAi` (required in a shared session) and the depth of an AI answer. */
export interface AssistantSendOptions {
  tier: AssistantTier;
  /** Absent in a private session (the server reads it as true); always sent in a shared one. */
  askAi?: boolean | undefined;
  /** The caller's own uploads in this session to put on the message; sent only when there are some. */
  attachmentIds?: readonly number[] | undefined;
}

export interface AssistantSessionPatch {
  name?: string | undefined;
  scope?: AssistantScopeEntity[] | undefined;
  categories?: Record<string, boolean> | undefined;
  defaultTier?: AssistantTier | undefined;
}

export const assistantApi = {
  /** GET /v1/me/assistant/options — the category registry and what can be scoped now. */
  async options(): Promise<AssistantOptions> {
    return normalizeOptions(await request<WireOptions>('GET', `${BASE}/options`));
  },
  /** GET /v1/me/assistant/sessions?status= — newest first. */
  async list(status: AssistantSessionStatus): Promise<AssistantSession[]> {
    const wire = await request<WireSession[]>('GET', `${BASE}/sessions`, {
      query: { status },
    });
    return wire.map(normalizeSession);
  },
  /**
   * POST …/sessions/support/ensure — the person's Help & feedback session, made on the first call
   * and found on every later one (idempotent; free, no subscription). Call it once per visit.
   */
  async ensureSupport(): Promise<AssistantSession> {
    return normalizeSession(await request<WireSession>('POST', `${BASE}/sessions/support/ensure`));
  },
  async get(sessionId: number): Promise<AssistantSession> {
    return normalizeSession(await request<WireSession>('GET', `${BASE}/sessions/${sessionId}`));
  },
  async create(body: AssistantSessionCreate): Promise<AssistantSession> {
    const json: AssistantSessionCreateRequest = {
      name: body.name,
      scope: body.scope.map(({ kind, id }) => ({ kind, id })),
      categories: body.categories,
      ...(body.defaultTier !== undefined ? { defaultTier: body.defaultTier } : {}),
      ...(body.visibility !== undefined ? { visibility: body.visibility } : {}),
    };
    return normalizeSession(await request<WireSession>('POST', `${BASE}/sessions`, { json }));
  },
  /** PATCH — sends only the fields given ("omit to leave unchanged"). */
  async patch(sessionId: number, body: AssistantSessionPatch): Promise<AssistantSession> {
    const json: AssistantSessionPatchRequest = {
      ...(body.name !== undefined ? { name: body.name } : {}),
      ...(body.scope !== undefined
        ? { scope: body.scope.map(({ kind, id }) => ({ kind, id })) }
        : {}),
      ...(body.categories !== undefined ? { categories: body.categories } : {}),
      ...(body.defaultTier !== undefined ? { defaultTier: body.defaultTier } : {}),
    };
    return normalizeSession(
      await request<WireSession>('PATCH', `${BASE}/sessions/${sessionId}`, { json }),
    );
  },
  async archive(sessionId: number): Promise<AssistantSession> {
    return normalizeSession(
      await request<WireSession>('POST', `${BASE}/sessions/${sessionId}/archive`),
    );
  },
  async restore(sessionId: number): Promise<AssistantSession> {
    return normalizeSession(
      await request<WireSession>('POST', `${BASE}/sessions/${sessionId}/restore`),
    );
  },
  async remove(sessionId: number): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/sessions/${sessionId}`);
  },
  /**
   * GET …/messages — one page, oldest first within it (TMMAPI-0184 U2). At most one id is sent (two
   * are a 400): `afterId` reads what's after it, for polling (`hasMore`: newer lines remain); no id
   * reads the newest page, `beforeId` pages back from it, and `aroundId` reads a window around a
   * line (`hasMore`, for all three: older lines remain).
   */
  async messages(
    sessionId: number,
    at?: AssistantMessagesAt,
    limit?: number,
  ): Promise<AssistantMessagePage> {
    const query = { ...at, ...(limit !== undefined ? { limit } : {}) };
    const wire = await request<WireMessagePage>('GET', `${BASE}/sessions/${sessionId}/messages`, {
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
    return {
      items: wire.items.map(normalizeMessage),
      hasMore: wire.hasMore,
      hasNewer: wire.hasNewer === true,
    };
  },
  /**
   * Everything after `afterId`, page by page (`hasMore`: newer lines remain): the live poll and the
   * read after a turn. Only ever what's new, never a whole transcript.
   */
  async messagesAfter(sessionId: number, afterId: number): Promise<AssistantMessage[]> {
    const out: AssistantMessage[] = [];
    let cursor = afterId;
    for (;;) {
      const page = await assistantApi.messages(sessionId, { afterId: cursor });
      out.push(...page.items);
      const last = page.items.at(-1);
      if (!page.hasMore || last === undefined) return out;
      cursor = last.id;
    }
  },
  /**
   * GET …/messages/search?q= — the session's lines holding `q` (2–100 characters once trimmed),
   * newest first, each with a snippet; `beforeId` (the last hit's id) reads the next older page.
   */
  async searchMessages(
    sessionId: number,
    q: string,
    beforeId?: number,
  ): Promise<AssistantSearchPage> {
    const wire = await request<WireSearchPage>(
      'GET',
      `${BASE}/sessions/${sessionId}/messages/search`,
      { query: { q, ...(beforeId !== undefined ? { beforeId } : {}) } },
    );
    return normalizeSearchPage(wire);
  },
  /**
   * GET /me/assistant/search?q= — the same across every session the caller lists (never another
   * person's private room), newest first, each hit with its session. `beforeId` is the previous
   * page's `nextBeforeId`, passed back as served: an opaque cursor (TMMAPI-0185 U1).
   */
  async searchAll(q: string, beforeId?: string): Promise<AssistantSearchPage> {
    const wire = await request<WireSearchPage>('GET', `${BASE}/search`, {
      query: { q, ...(beforeId !== undefined ? { beforeId } : {}) },
    });
    return normalizeSearchPage(wire);
  },
  /**
   * POST …/messages `{text, askAi?, tier?, attachmentIds?}` → the message, and the turn to poll
   * when it asked the assistant. A message to the other members (`askAi: false`) sends no tier.
   */
  async send(
    sessionId: number,
    text: string,
    { tier, askAi, attachmentIds }: AssistantSendOptions,
  ): Promise<AssistantMessageSent> {
    const files =
      attachmentIds !== undefined && attachmentIds.length > 0
        ? { attachmentIds: [...attachmentIds] }
        : {};
    const json: AssistantMessageRequest =
      askAi === false
        ? { text, askAi, ...files }
        : { text, ...(askAi !== undefined ? { askAi } : {}), tier, ...files };
    const wire = await request<AssistantMessageAccepted>(
      'POST',
      `${BASE}/sessions/${sessionId}/messages`,
      { json },
    );
    return { messageId: wire.messageId, turnId: wire.turnId };
  },
  /**
   * POST …/sessions/{sharedId}/breakout — the caller's private room on a shared session, made on the
   * first call and returned on every later one (idempotent; free, no subscription).
   */
  async openBreakout(sharedId: number): Promise<AssistantSession> {
    return normalizeSession(
      await request<WireSession>('POST', `${BASE}/sessions/${sharedId}/breakout`),
    );
  },
  /** POST …/attachments — multipart `file`; free, and the caller's alone until it's on a message. */
  async uploadAttachment(
    sessionId: number,
    file: File,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress'],
  ): Promise<AssistantAttachment> {
    const form = new FormData();
    form.append('file', file);
    return normalizeAttachment(
      await request<WireAttachment>('POST', `${BASE}/sessions/${sessionId}/attachments`, {
        form,
        ...(onUploadProgress !== undefined ? { onUploadProgress } : {}),
      }),
    );
  },
  /** DELETE …/attachments/{id} — the caller's own upload, while it's on no message. */
  async deleteAttachment(sessionId: number, attachmentId: number): Promise<void> {
    await request<unknown>('DELETE', `${BASE}/sessions/${sessionId}/attachments/${attachmentId}`);
  },
  /**
   * GET …/attachments/{id} — the file, for the session's members (bearer only, so a Blob). `signal`
   * aborts it (a picture that left the view, M8).
   */
  async downloadAttachment(
    sessionId: number,
    attachmentId: number,
    signal?: AbortSignal,
  ): Promise<DownloadResult> {
    return download(`${BASE}/sessions/${sessionId}/attachments/${attachmentId}`, {
      accept: '*/*',
      ...(signal !== undefined ? { signal } : {}),
    });
  },
  /**
   * PUT …/private-data — the caller's own private data in a shared session (every member sees it)
   * or in their breakout room (theirs alone), the whole set.
   */
  async putPrivateData(
    sessionId: number,
    body: { accountIds: number[]; categories: string[] },
  ): Promise<AssistantSession> {
    const json: AssistantPrivateDataRequest = {
      accountIds: [...body.accountIds],
      categories: [...body.categories],
    };
    return normalizeSession(
      await request<WireSession>('PUT', `${BASE}/sessions/${sessionId}/private-data`, { json }),
    );
  },
  /** POST …/read `{upToId}` — the caller has read up to this message (idempotent). */
  async markRead(sessionId: number, upToId: number): Promise<void> {
    const json: AssistantReadRequest = { upToId };
    await request<unknown>('POST', `${BASE}/sessions/${sessionId}/read`, { json });
  },
  async turn(sessionId: number, turnId: number): Promise<AssistantTurn> {
    return normalizeTurn(
      await request<WireTurn>('GET', `${BASE}/sessions/${sessionId}/turns/${turnId}`),
    );
  },
  /** POST …/turns/{turnId}/continue — resumes a NEEDS_SCOPE turn; poll the same turn again. */
  async continueTurn(sessionId: number, turnId: number): Promise<number> {
    const wire = await request<AssistantTurnAccepted>(
      'POST',
      `${BASE}/sessions/${sessionId}/turns/${turnId}/continue`,
    );
    return wire.turnId;
  },
  /**
   * POST …/turns/{turnId}/ticket-draft/dismiss — "Not now" on the caller's own support turn's draft
   * (TMMAPI-0185 U1): free and idempotent; the draft is then omitted from its line. `409
   * ASSISTANT_TURN_NOT_WAITING` (`details.reason` `TICKET_FILED`): it was filed.
   */
  async dismissTicketDraft(sessionId: number, turnId: number): Promise<AssistantTurn> {
    return normalizeTurn(
      await request<WireTurn>(
        'POST',
        `${BASE}/sessions/${sessionId}/turns/${turnId}/ticket-draft/dismiss`,
      ),
    );
  },
  async declineScope(sessionId: number, turnId: number): Promise<AssistantTurn> {
    return normalizeTurn(
      await request<WireTurn>(
        'POST',
        `${BASE}/sessions/${sessionId}/turns/${turnId}/decline-scope`,
      ),
    );
  },
  /** GET …/privacy[?turnId=] — with a turn, `sent` holds that turn's calls only. */
  async privacy(sessionId: number, turnId?: number): Promise<AssistantPrivacy> {
    return normalizePrivacy(
      await request<WirePrivacy>('GET', `${BASE}/sessions/${sessionId}/privacy`, {
        ...(turnId !== undefined ? { query: { turnId } } : {}),
      }),
    );
  },
};
